/* eslint-disable @next/next/no-img-element */

import classNames from 'classnames';
import { useMemo } from 'react';

import useAnimateOnVisible from '@/hooks/use-animate-on-visible';

import HygraphImage, { HygraphImageProps } from '@/components/hygraph-image';
import Row from '@/components/row';
import { Typography } from '@/components/typography';

import { RowLayout } from '@/enums/row-layout';
import Carousel, {
  CarouselItem,
  CarouselPerPage,
  CarouselVariant,
} from '@/features/carousel';

type TestimonialProps = {
  id: string;
  name: string;
  role: string;
  message: string;
  image: HygraphImageProps;
};
export const testimonials: TestimonialProps[] = [
  {
    id: `371385de-f8d7-4d98-9376-a303a009bf71`,
    name: 'Denice',
    role: 'Oncology RN',
    message:
      'I like that Nomad allows me the flexibility to choose my own assignments.',
    image: {
      url: 'https://media.graphassets.com/3pq3SyHUR9e8gEHbBWC5',
      description: '',
      height: 400,
      width: 400,
    },
  },
  {
    id: `371385de-f8d7-4d98-9376-a303a009bf72`,
    name: 'Terri N.',
    role: 'ICU RN',
    message:
      'I am with Nomad and LOVE them...they really love feedback and are open to hearing what nurses need and want.',
    image: {
      url: 'https://media.graphassets.com/5KFTzjNQNKHWp5bzIJqw',
      description: '',
      height: 400,
      width: 400,
    },
  },
  {
    id: `371385de-f8d7-4d98-9376-a303a009bf73`,
    name: 'Shane',
    role: 'ICU RN',
    message:
      "It's just a lot simpler. I would absolutely recommend Nomad to other nurses and often do!",
    image: {
      url: 'https://media.graphassets.com/ixDpIwTPaKTokFLs44An',
      description: '',
      height: 400,
      width: 400,
    },
  },
  {
    id: `371385de-f8d7-4d98-9376-a303a009bf74`,
    name: 'Sydney M.',
    role: 'ER RN',
    message:
      'I appreciate the transparency and support Nomad has to offer. So glad I chose Nomad for my first contract!',
    image: {
      url: 'https://media.graphassets.com/Qv8EouswQ8i2QcaUmB2A',
      description: '',
      height: 400,
      width: 400,
    },
  },
  {
    id: `371385de-f8d7-4d98-9376-a303a009bf75`,
    name: 'Ian',
    role: 'ICU RN',
    message:
      "Nomad has had my back with every circumstance. They put me first, basically, and that's been really refreshing.",
    image: {
      url: 'https://media.graphassets.com/10AJ74BTgcM1YziFTBOw',
      description: '',
      height: 400,
      width: 400,
    },
  },
  {
    id: `371385de-f8d7-4d98-9376-a303a009bf76`,
    name: 'Vincent C.',
    role: 'ICU RN',
    message:
      "[Colleagues] get upset when they learn how much I make with Nomad vs. how much they make. We're on the same assignment. Same job.",
    image: {
      url: 'https://media.graphassets.com/rR5ZhHY2RVC97wILRDmK',
      description: '',
      height: 400,
      width: 400,
    },
  },
  {
    id: `371385de-f8d7-4d98-9376-a303a009bf77`,
    name: 'Carrie',
    role: 'OR RN',
    message:
      'I have never worked with any people that just cared about their nurses. I just appreciate them!',
    image: {
      url: 'https://media.graphassets.com/yDRFz4S2RO6bU1bzfQhl',
      description: '',
      height: 400,
      width: 400,
    },
  },
  {
    id: `371385de-f8d7-4d98-9376-a303a009bf78`,
    name: 'Dean',
    role: 'PCU RN',
    message:
      "The jobs are online and it's very transparent, so you see what the places are like and what you're gonna get paid, all up front.",
    image: {
      url: 'https://media.graphassets.com/2PWfMF0LQGK5bXQhUZwf',
      description: '',
      height: 400,
      width: 400,
    },
  },
];

export default function Testimonials() {
  const testimonialsCarouselItems = useMemo<CarouselItem[]>(
    () =>
      testimonials.map((testimonial) => ({
        id: testimonial.id,
        component: (
          <TestimonialCard
            testimonial={testimonial}
            className='overflow-y-hidden'
            animate={false}
          />
        ),
      })),
    []
  );
  return (
    <>
      <Row
        layout={RowLayout.FOUR_COLUMN}
        className='hidden md:flex md:max-w-full lg:max-w-full'
      >
        {testimonials.map((testimonial) => (
          <TestimonialCard key={testimonial.id} testimonial={testimonial} />
        ))}
      </Row>
      <Row layout={RowLayout.FULL_WIDE} className='flex md:hidden'>
        <Carousel
          variant={CarouselVariant.PAGINATED}
          items={testimonialsCarouselItems}
          perPage={CarouselPerPage.ONE}
          showPagination
        />
      </Row>
    </>
  );
}

type TestimonialCardProps = {
  testimonial: TestimonialProps;
  className?: string;
  animate?: boolean;
};

const TestimonialCard = ({
  testimonial,
  className,
  animate = true,
}: TestimonialCardProps) => {
  const { ref, animateClassName } = useAnimateOnVisible();

  return (
    <div
      ref={ref}
      key={testimonial.id}
      className={classNames(
        'block h-full divide-y divide-gray-200 rounded-lg bg-white shadow',
        animate && animateClassName,
        className
      )}
    >
      <div className='flex flex-col space-y-2 p-8'>
        <Typography.Text>{`"${testimonial.message}"`}</Typography.Text>
        <div className='flex w-full items-center justify-between space-x-4 pt-2'>
          <HygraphImage
            sizes='4rem'
            quality={99}
            className='h-16 w-16 flex-shrink-0 rounded-full'
            {...testimonial.image}
          />
          <div className='flex-1 text-sm uppercase'>
            <Typography.TextSmall weight='bold'>
              {testimonial.name}
            </Typography.TextSmall>
            <Typography.TextSmall>{testimonial.role}</Typography.TextSmall>
          </div>
        </div>
      </div>
    </div>
  );
};
